import type { components } from '@multaqa/api-client'
import {
  type UseMutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useRef } from 'react'
import { api } from './api'

export type AnalysisTag = components['schemas']['AnalysisTagResource']

export interface AnalysisTagBody {
  name: Record<string, string>
}

export type AnalysisTagUpdateBody = AnalysisTagBody & { is_active?: boolean }

type AnalysisTagSync = components['schemas']['SyncAnalysisTagsRequest']

export function analysisTagIdsFrom(tags: unknown): string[] {
  if (!Array.isArray(tags)) return []
  return tags.flatMap((tag: unknown) => {
    if (typeof tag !== 'object' || tag === null || !('id' in tag)) return []
    return typeof tag.id === 'string' ? [tag.id] : []
  })
}

export const analysisTagKeys = {
  list: (companyId: string | null) =>
    ['companies', companyId, 'accounting', 'analysis-tags'] as const,
}

export function useAnalysisTags(companyId: string | null) {
  return useQuery<AnalysisTag[], Error>({
    queryKey: analysisTagKeys.list(companyId),
    enabled: Boolean(companyId),
    queryFn: async () => {
      if (!companyId) return []
      const response = await api.GET('/accounting/analysis-tags', {
        headers: { 'X-Company-Id': companyId },
      })
      if (!response.data) throw new Error('Analysis tags returned no data.')
      return response.data.data
    },
  })
}

export function useSyncInvoiceAnalysisTags(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<void, Error, { invoiceId: string; body: AnalysisTagSync }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const companyRef = useRef(companyId)
  companyRef.current = companyId
  return useMutation<void, Error, { invoiceId: string; body: AnalysisTagSync }>({
    ...options,
    mutationFn: async ({ invoiceId, body }) => {
      const response = await api.PUT('/accounting/invoices/{invoice}/analysis-tags', {
        headers: { 'X-Company-Id': companyRef.current ?? '' },
        params: { path: { invoice: invoiceId } },
        body,
      })
      if (response.error)
        throw new Error(response.error.message ?? 'Failed to update invoice analysis tags')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'accounting'] })
      options?.onSuccess?.(...args)
    },
  })
}

export function useSyncJournalEntryAnalysisTags(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<void, Error, { entryId: string; body: AnalysisTagSync }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const companyRef = useRef(companyId)
  companyRef.current = companyId
  return useMutation<void, Error, { entryId: string; body: AnalysisTagSync }>({
    ...options,
    mutationFn: async ({ entryId, body }) => {
      const response = await api.PUT('/accounting/journal-entries/{entry}/analysis-tags', {
        headers: { 'X-Company-Id': companyRef.current ?? '' },
        params: { path: { entry: entryId } },
        body,
      })
      if (response.error)
        throw new Error(response.error.message ?? 'Failed to update journal entry analysis tags')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'accounting'] })
      options?.onSuccess?.(...args)
    },
  })
}

export function useCreateAnalysisTag(companyId: string | null) {
  const queryClient = useQueryClient()
  const companyRef = useRef(companyId)
  companyRef.current = companyId
  return useMutation<AnalysisTag, Error, AnalysisTagBody>({
    mutationFn: async (body) => {
      const response = await api.POST('/accounting/analysis-tags', {
        headers: { 'X-Company-Id': companyRef.current ?? '' },
        // Scramble infers the localized name as a list from its closure rule.
        body: body as unknown as components['schemas']['AnalysisTagSaveRequest'],
      })
      if (!response.data) throw new Error('Analysis tag creation returned no data.')
      return response.data.data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: analysisTagKeys.list(companyId) }),
  })
}

export function useUpdateAnalysisTag(companyId: string | null) {
  const queryClient = useQueryClient()
  const companyRef = useRef(companyId)
  companyRef.current = companyId
  return useMutation<AnalysisTag, Error, { tagId: string; body: AnalysisTagUpdateBody }>({
    mutationFn: async ({ tagId, body }) => {
      const response = await api.PATCH('/accounting/analysis-tags/{analysisTag}', {
        headers: { 'X-Company-Id': companyRef.current ?? '' },
        params: { path: { analysisTag: tagId } },
        body: body as unknown as components['schemas']['AnalysisTagSaveRequest'],
      })
      if (!response.data) throw new Error('Analysis tag update returned no data.')
      return response.data.data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: analysisTagKeys.list(companyId) }),
  })
}

export function useDeleteAnalysisTag(companyId: string | null) {
  const queryClient = useQueryClient()
  const companyRef = useRef(companyId)
  companyRef.current = companyId
  return useMutation<void, Error, { tagId: string }>({
    mutationFn: async ({ tagId }) => {
      await api.DELETE('/accounting/analysis-tags/{analysisTag}', {
        headers: { 'X-Company-Id': companyRef.current ?? '' },
        params: { path: { analysisTag: tagId } },
      })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: analysisTagKeys.list(companyId) }),
  })
}
